// Outbound sourcing channels — labels, colors, ordering.
// Single source of truth used by both server (zod validation) and client (chips).
//
// When a new marketplace gets added:
//   1. Add the enum value to `sourcing_channel` in schema.ts
//   2. Add a `SOURCING_CHANNELS` entry below
//   3. Generate + apply a migration
// No other code changes needed — the chip UI iterates this list.

export type SourcingChannel = 'partsbase' | 'ils' | 'stockmarket' | 'email_blast' | 'website' | 'not_available';

/**
 * `not_available` is special: not a marketplace you post to, but a terminal
 * marker meaning the part couldn't be sourced. Marking it sets the line to
 * `no_quote` (broker-confirmed via a dialog). Kept in this list so it renders
 * as a chip on the same toggle plumbing.
 */
export const NOT_AVAILABLE_CHANNEL = 'not_available' as const;

export const SOURCING_CHANNEL_VALUES: readonly SourcingChannel[] = [
  'partsbase',
  'ils',
  'stockmarket',
  'email_blast',
  'website',
  'not_available',
] as const;

export interface SourcingChannelConfig {
  /** Human-readable label shown in the chip. */
  label: string;
  /** Short label for tight spaces (RFQ list rows). */
  shortLabel: string;
  /**
   * Tailwind classes for the "posted" state. Kept as full class names (not
   * dynamically built) so the Tailwind JIT picks them up at build time.
   * Requires the `shared/` directory in tailwind.config.ts `content` paths.
   */
  postedClass: string;
  /** Tailwind classes for the "not posted" (ghost) state. */
  ghostClass: string;
}

export const SOURCING_CHANNELS: Record<SourcingChannel, SourcingChannelConfig> = {
  partsbase: {
    label: 'PartsBase',
    shortLabel: 'PB',
    postedClass: 'bg-blue-600 text-white border-blue-600',
    ghostClass: 'bg-transparent text-blue-700 border-blue-300 hover:bg-blue-50',
  },
  ils: {
    label: 'ILS',
    shortLabel: 'ILS',
    postedClass: 'bg-purple-600 text-white border-purple-600',
    ghostClass: 'bg-transparent text-purple-700 border-purple-300 hover:bg-purple-50',
  },
  stockmarket: {
    label: 'StockMarket.aero',
    shortLabel: 'SM',
    postedClass: 'bg-teal-600 text-white border-teal-600',
    ghostClass: 'bg-transparent text-teal-700 border-teal-300 hover:bg-teal-50',
  },
  email_blast: {
    label: 'Email blast',
    shortLabel: 'Email',
    postedClass: 'bg-slate-700 text-white border-slate-700',
    ghostClass: 'bg-transparent text-slate-700 border-slate-300 hover:bg-slate-50',
  },
  website: {
    label: 'Website',
    shortLabel: 'Web',
    postedClass: 'bg-emerald-600 text-white border-emerald-600',
    ghostClass: 'bg-transparent text-emerald-700 border-emerald-300 hover:bg-emerald-50',
  },
  not_available: {
    label: 'Not Available',
    shortLabel: 'N/A',
    postedClass: 'bg-red-600 text-white border-red-600',
    ghostClass: 'bg-transparent text-red-700 border-red-300 hover:bg-red-50',
  },
};

export function isSourcingChannel(value: unknown): value is SourcingChannel {
  return typeof value === 'string' && (SOURCING_CHANNEL_VALUES as readonly string[]).includes(value);
}
